import {SyncDiagnostics} from './SyncDiagnostics';
import {PinKeypad} from './UiKit';
import {recordSyncDiagnostic,syncFailureMessage} from './sync-diagnostics';
import {useEffect,useMemo,useState,type ReactNode} from 'react';
import {resolveSmmRuntimePort} from './runtime';
import {
  pairSmmLan,
  probeSmmLan,
  readSmmLanPwaConfig,
  saveSmmLanPwaConfig,
  type SmmLanPwaConfig,
} from './pwa-lan';
import {
  listSmmStaff,
  isSmmStaffSessionExplicitlyInvalid,
  forgetSmmStaffSession,
  readSmmStaffSession,
  verifySmmStaff,
  type SmmStaffDirectoryItem,
  type SmmStaffSession,
} from './pwa-staff';
import type {SmmReadModelSnapshot} from './product-types';
import './stage0.css';

const SPLASH_MS=650;
const PROBE_TIMEOUT_MS=3500;
const LAN_PROBE_TIMEOUT_MS=2200;
const LAST_OBSERVED_KEY='mfk.smm.stage0.last-observed.v1';

function uiAcceptanceBypass(){
  if(typeof window==='undefined')return false;
  const query=new URLSearchParams(window.location.search);
  const host=window.location.hostname.toLowerCase();
  const isLocal=host==='localhost'||host==='127.0.0.1';
  const isAcceptance=host.startsWith('smm-acceptance-')&&host.endsWith('.yeungyi88.workers.dev');
  return isAcceptance||(isLocal&&query.get('ui-bypass')==='1');
}

type ProbeState='CHECKING'|'READY'|'ERROR';
type StartupProbe=Readonly<{state:'READY';snapshot:SmmReadModelSnapshot}>|Readonly<{state:'LOGIN'}>|Readonly<{state:'ERROR';reason:unknown}>;
type LanState='UNCONFIGURED'|'CHECKING'|'READY'|'ERROR';

function withTimeout<T>(promise:Promise<T>,ms:number):Promise<T>{
  return new Promise<T>((resolve,reject)=>{
    const timer=window.setTimeout(()=>reject(new Error('SMM_STAGE0_PROBE_TIMEOUT')),ms);
    promise.then(value=>{window.clearTimeout(timer);resolve(value)},reason=>{window.clearTimeout(timer);reject(reason)});
  });
}

/** Classify the real startup read without changing its authentication contract. */
export async function readStageZeroProbe(readSnapshot:()=>Promise<SmmReadModelSnapshot>,context:{online:boolean;hasSession:boolean}):Promise<StartupProbe>{
  try{return {state:'READY',snapshot:await withTimeout(readSnapshot(),PROBE_TIMEOUT_MS)};}
  catch(reason){
    const code=reason instanceof Error?reason.message:'';
    if(code==='SMM_STAGE0_PROBE_TIMEOUT')recordSyncDiagnostic('startup',null,code);
    const unauthorized=(code==='SYNC_PORT_UNAUTHORIZED'||code==='SECURITY_UNAUTHORIZED')&&typeof reason==='object'&&reason!==null&&'httpStatus'in reason&&reason.httpStatus===401;
    if(context.online&&code==='SMM_SYNC_SESSION_REQUIRED'&&!context.hasSession)return {state:'LOGIN'};
    if(context.online&&unauthorized){
      const session=readSmmStaffSession();
      if(session){
        try{
          const invalid=await withTimeout(isSmmStaffSessionExplicitlyInvalid(session),PROBE_TIMEOUT_MS);
          if(invalid&&readSmmStaffSession()?.sessionToken===session.sessionToken){
            forgetSmmStaffSession();
            return {state:'LOGIN'};
          }
        }catch{/* Keep the trusted session and offline entry when validation is unavailable. */}
      }
    }
    return {state:'ERROR',reason};
  }
}

function readLastObservedAt():string|null{
  try{return localStorage.getItem(LAST_OBSERVED_KEY)}catch{return null}
}

function rememberLastObservedAt(value:string){
  try{localStorage.setItem(LAST_OBSERVED_KEY,value)}catch{}
}

function formatObservedAt(value:string|null){
  if(!value)return '未有成功同步紀錄';
  const date=new Date(value);
  if(Number.isNaN(date.getTime()))return '未有成功同步紀錄';
  return new Intl.DateTimeFormat('zh-HK',{
    month:'2-digit',
    day:'2-digit',
    hour:'2-digit',
    minute:'2-digit',
    hour12:false,
  }).format(date);
}

function initialLanForm(){
  const current=readSmmLanPwaConfig();
  return {
    host:current?.host??'',
    port:String(current?.port??17831),
    deviceId:current?.deviceId??'',
    pairingToken:'',
  };
}

export function StageZeroGate({children}:{children:ReactNode}){
  const initialSession=useMemo(()=>readSmmStaffSession(),[]);
  const bypass=useMemo(()=>uiAcceptanceBypass(),[]);
  const [splashDone,setSplashDone]=useState(false);
  const [probeState,setProbeState]=useState<ProbeState|'LOGIN'>('CHECKING');
  const [snapshot,setSnapshot]=useState<SmmReadModelSnapshot|null>(null);
  const [probeMessage,setProbeMessage]=useState('暫時未能連接門店服務，請檢查網絡後再試。');
  const [staffSession,setStaffSession]=useState<SmmStaffSession|null>(initialSession);
  const [offlineBypass,setOfflineBypass]=useState(false);
  const [lastObservedAt,setLastObservedAt]=useState<string|null>(()=>readLastObservedAt());

  const probe=async()=>{
    const port=resolveSmmRuntimePort();
    if(!port){
      console.warn('SMM_STAGE0_PROBE_DIAGNOSTIC','runtime port unavailable');
      setProbeState('ERROR');
      setProbeMessage('門店服務暫時未準備好，請稍後再試。');
      return;
    }
    setProbeState('CHECKING');
    setProbeMessage('暫時未能連接門店服務，請檢查網絡後再試。');
    const result=await readStageZeroProbe(()=>port.readStartupSnapshot?port.readStartupSnapshot():port.readSnapshot(),{online:navigator.onLine,hasSession:Boolean(readSmmStaffSession())});
    if(result.state==='LOGIN'){
      setStaffSession(null);
      setOfflineBypass(false);
      setProbeState('LOGIN');
    }else if(result.state==='READY'){
      const next=result.snapshot;
      setSnapshot(next);
      setLastObservedAt(next.observedAt);
      rememberLastObservedAt(next.observedAt);
      setProbeState('READY');
    }else{
      console.warn('SMM_STAGE0_PROBE_DIAGNOSTIC',result.reason);
      setProbeState('ERROR');
      setProbeMessage(navigator.onLine
        ?syncFailureMessage()
        :'目前裝置未連接網絡；恢復網絡後可重新連線。');
    }
  };

  useEffect(()=>{
    const timer=window.setTimeout(()=>setSplashDone(true),SPLASH_MS);
    void probe();
    return()=>window.clearTimeout(timer);
  },[]);

  if(!splashDone)return <StageZeroSplash/>;

  // Owner-authorised UI acceptance bypass: presentation review must not depend on Internet/LAN auth.
  // It is deliberately limited to the acceptance hostname, localhost, or explicit ?ui-bypass=1.
  if(bypass)return <>{children}</>;

  if(offlineBypass&&staffSession)return <>{children}</>;

  if(probeState==='CHECKING')return <StageZeroConnectionChecking/>;

  const loggedIn=(session:SmmStaffSession)=>{setStaffSession(session);void probe();};
  if(probeState==='LOGIN')return <StageZeroStaffLogin onSuccess={loggedIn}/>;

  if(probeState==='ERROR'){
    return <StageZeroConnectionRecovery
      message={probeMessage}
      lastObservedAt={lastObservedAt??snapshot?.observedAt??null}
      canEnterOffline={Boolean(staffSession)}
      onRetry={()=>void probe()}
      onOffline={()=>{if(staffSession)setOfflineBypass(true)}}
    />;
  }

  if(snapshot?.connectionPath!=='LAN'&&!staffSession){
    return <StageZeroStaffLogin onSuccess={loggedIn}/>;
  }

  return <>{children}</>;
}

function BrandLockup({compact=false}:{compact?:boolean}){
  return <div className={compact?'stage0-brand compact':'stage0-brand'}>
    <img className="stage0-brand-logo" src="/brand/morefun-logo.webp" alt="磨飯 More Fun"/>
    <span className="stage0-product-label">手機點單</span>
  </div>;
}

function Stage0MascotPair({mode='duo'}:{mode?:'duo'|'female'}){
  if(mode==='female')return <img className="stage0-mascot stage0-mascot-recovery" src="/brand/stage0/stage0-recovery-female.webp" alt="" aria-hidden="true"/>;
  return <span className="stage0-mascot-pair" aria-hidden="true">
    <img src="/brand/stage0/stage0-splash-male.webp" alt=""/>
    <img src="/brand/stage0/stage0-female.webp" alt=""/>
  </span>;
}

function Stage0Mascot({scene}:{scene:'splash'|'login'|'checking'|'recovery'}){
  if(scene==='login')return <Stage0MascotPair/>;
  const src=scene==='splash'
    ?'/brand/stage0/stage0-splash-male.webp'
    :scene==='recovery'
      ?'/brand/stage0/stage0-recovery-female.webp'
      :'/brand/stage0/stage0-female.webp';
  return <img className={`stage0-mascot stage0-mascot-${scene}`} src={src} alt="" aria-hidden="true"/>;
}

function StageZeroSplash(){
  return <main className="stage0-shell stage0-splash" aria-busy="true">
    <span className="stage0-feather stage0-feather-a" aria-hidden="true"/>
    <span className="stage0-feather stage0-feather-b" aria-hidden="true"/>
    <section className="stage0-center">
      <BrandLockup/>
      <div className="stage0-slogan">
        <strong>前線好幫手</strong>
        <span>快速點單 · 清楚跟單 · 隨時掌握</span>
      </div>
      <Stage0Mascot scene="splash"/>
      <div className="stage0-progress" aria-label="啟動中"><i/></div>
      <small className="stage0-footnote">正在準備手機點單…</small>
    </section>
  </main>;
}

function StageZeroConnectionChecking(){
  return <main className="stage0-shell stage0-connection-page">
    <section className="stage0-card stage0-check-card">
      <BrandLockup compact/>
      <div className="stage0-status-orb loading" aria-hidden="true"><span/></div>
      <h1>正在連線</h1>
      <p>正在確認 Internet、店內 LAN 同最新門店資料。</p>
      <p role="status">等緊門店回覆…</p>
      <Stage0Mascot scene="checking"/>
    </section>
  </main>;
}

function StageZeroConnectionRecovery({
  message,
  lastObservedAt,
  canEnterOffline,
  onRetry,
  onOffline,
}:{
  message:string;
  lastObservedAt:string|null;
  canEnterOffline:boolean;
  onRetry:()=>void;
  onOffline:()=>void;
}){
  const [internetOnline,setInternetOnline]=useState(()=>navigator.onLine);
  const [lanState,setLanState]=useState<LanState>(()=>readSmmLanPwaConfig()?'CHECKING':'UNCONFIGURED');
  const [lanMessage,setLanMessage]=useState(readSmmLanPwaConfig()?'檢查中':'未設定');
  const [pairOpen,setPairOpen]=useState(false);
  const [pairing,setPairing]=useState(false);
  const [pairMessage,setPairMessage]=useState<string|null>(null);
  const [lanForm,setLanForm]=useState(initialLanForm);

  const checkLan=async(config:SmmLanPwaConfig)=>{
    setLanState('CHECKING');
    setLanMessage('檢查中');
    try{
      await withTimeout(probeSmmLan(config),LAN_PROBE_TIMEOUT_MS);
      setLanState('READY');
      setLanMessage('可連線');
    }catch(reason){
      console.warn('SMM_STAGE0_LAN_DIAGNOSTIC',reason);
      setLanState('ERROR');
      setLanMessage('未能連線');
    }
  };

  useEffect(()=>{
    const online=()=>setInternetOnline(true);
    const offline=()=>setInternetOnline(false);
    window.addEventListener('online',online);
    window.addEventListener('offline',offline);
    const config=readSmmLanPwaConfig();
    if(config)void checkLan(config);
    return()=>{
      window.removeEventListener('online',online);
      window.removeEventListener('offline',offline);
    };
  },[]);

  const pairLan=async()=>{
    const port=Number(lanForm.port);
    if(!lanForm.host.trim()||!lanForm.deviceId.trim()||!Number.isSafeInteger(port)||port<1||port>65535){
      setPairMessage('請完整輸入門店主機、Port 同裝置 ID。');
      return;
    }
    setPairing(true);
    setPairMessage(null);
    const config:SmmLanPwaConfig={
      host:lanForm.host.trim(),
      port,
      deviceId:lanForm.deviceId.trim(),
      ...(lanForm.pairingToken.trim()?{pairingToken:lanForm.pairingToken.trim()}:{}),
    };
    try{
      saveSmmLanPwaConfig(config);
      await withTimeout(pairSmmLan(config),LAN_PROBE_TIMEOUT_MS);
      setLanForm(current=>({...current,pairingToken:''}));
      setPairMessage('LAN 配對完成。');
      setPairOpen(false);
      await checkLan(config);
      onRetry();
    }catch(reason){
      console.warn('SMM_STAGE0_PAIR_DIAGNOSTIC',reason);
      setPairMessage('LAN 配對未完成，請檢查主機、裝置 ID 同配對碼後再試。');
      setLanState('ERROR');
      setLanMessage('未配對');
    }finally{
      setPairing(false);
    }
  };

  return <main className="stage0-shell">
    <section className="stage0-card stage0-recovery-card">
      <BrandLockup compact/>
      <div className="stage0-page-intro">
        <div>
          <span>連線與恢復</span>
          <h1>重新連接門店</h1>
          <p>{message}</p>
        </div>
        <div className="stage0-status-icon error" aria-hidden="true">!</div>
      </div>

      <div className="stage0-connection-grid" aria-label="連線狀態">
        <div>
          <span>Internet</span>
          <strong className={internetOnline?'ok':'bad'}>{internetOnline?'裝置在線':'裝置離線'}</strong>
        </div>
        <div>
          <span>LAN</span>
          <strong className={lanState==='READY'?'ok':lanState==='CHECKING'?'pending':'bad'}>{lanMessage}</strong>
        </div>
        <div className="wide">
          <span>最後觀察時間</span>
          <strong>{formatObservedAt(lastObservedAt)}</strong>
        </div>
      </div>

      <SyncDiagnostics/>
      <button className="stage0-primary" onClick={onRetry}>重新檢查</button>
      <button className="stage0-secondary" onClick={()=>setPairOpen(open=>!open)}>
        {readSmmLanPwaConfig()?'重新配對 LAN':'設定店內 LAN'}
      </button>

      {pairOpen?<section className="stage0-pair-panel">
        <label className="stage0-field"><span>門店主機</span><input value={lanForm.host} onChange={event=>setLanForm(current=>({...current,host:event.target.value}))} placeholder="例如 192.168.1.20"/></label>
        <label className="stage0-field"><span>Port</span><input inputMode="numeric" value={lanForm.port} onChange={event=>setLanForm(current=>({...current,port:event.target.value.replace(/\D/g,'').slice(0,5)}))}/></label>
        <label className="stage0-field"><span>裝置 ID</span><input value={lanForm.deviceId} onChange={event=>setLanForm(current=>({...current,deviceId:event.target.value}))} placeholder="此手機嘅裝置 ID"/></label>
        <label className="stage0-field"><span>配對碼</span><input type="password" autoComplete="one-time-code" value={lanForm.pairingToken} onChange={event=>setLanForm(current=>({...current,pairingToken:event.target.value}))} placeholder="輸入收銀機顯示嘅配對碼"/></label>
        {pairMessage?<div className="stage0-inline-message" role="status">{pairMessage}</div>:null}
        <button className="stage0-primary" disabled={pairing} onClick={()=>void pairLan()}>{pairing?'配對中…':'儲存並配對'}</button>
      </section>:null}

      <button className="stage0-secondary stage0-offline-button" disabled={!canEnterOffline} onClick={onOffline}>繼續離線工作</button>
      <Stage0Mascot scene="recovery"/>
      <small className="stage0-security">
        {canEnterOffline
          ?'購物車保留住，連返門店先落單。'
          :'連線後先登入。'}
      </small>
    </section>
  </main>;
}

export function smmStaffLoginError(reason:unknown){
  return reason instanceof Error&&reason.message.includes('STAFF_CLOUD_PIN_RESET_REQUIRED')?'此帳號未啟用手機登入。請在 Admin 員工設定重新輸入並保存同一 PIN，再回來登入。':'登入編號或 PIN 未能驗證，請確認後再試。';
}
function StageZeroStaffLogin({onSuccess}:{onSuccess:(session:SmmStaffSession)=>void}){
  const [staff,setStaff]=useState<readonly SmmStaffDirectoryItem[]>([]);
  const [staffId,setStaffId]=useState('');
  const [pin,setPin]=useState('');
  const [loading,setLoading]=useState(false);
  const [directoryLoading,setDirectoryLoading]=useState(true);
  const [error,setError]=useState<string|null>(null);

  const loadDirectory=async()=>{
    setDirectoryLoading(true);
    setError(null);
    try{
      const rows=await listSmmStaff();
      setStaff(rows);
      if(!staffId&&rows[0])setStaffId(rows[0].loginId||rows[0].staffId);
    }catch(reason){
      console.warn('SMM_STAGE0_STAFF_DIRECTORY_DIAGNOSTIC',reason);
      setError('暫時未能讀取員工名單；可以直接輸入登入編號。');
    }finally{
      setDirectoryLoading(false);
    }
  };

  useEffect(()=>{void loadDirectory()},[]);

  const submit=async()=>{
    if(!staffId.trim()){
      setError('請先選擇或輸入登入編號。');
      return;
    }
    setLoading(true);
    setError(null);
    try{
      const session=await verifySmmStaff(staffId.trim(),pin);
      onSuccess(session);
    }catch(reason){
      console.warn('SMM_STAGE0_STAFF_VERIFY_DIAGNOSTIC',reason);
      setError(smmStaffLoginError(reason));
    }finally{
      setLoading(false);
    }
  };

  return <main className="stage0-shell ui-login-shell">
    <section className="stage0-card stage0-login-card">
      <header className="v3-login-header"><BrandLockup compact/><div className="ui-login-context"><h1>員工登入</h1><p>磨飯・手機 POS</p></div></header>

      {staff.length?<div className="ui-staff-picker" aria-label="選擇店員">{staff.map(row=><button key={row.staffId} type="button" className={staffId===(row.loginId||row.staffId)?'active':''} aria-pressed={staffId===(row.loginId||row.staffId)} disabled={loading} onClick={()=>{setStaffId(row.loginId||row.staffId);setPin('');setError(null)}}><b aria-hidden="true">{row.displayName.slice(0,1)}</b><span>{row.displayName}</span></button>)}</div>:null}

      {!staff.length?<label className="stage0-field">
        <span>登入編號</span>
        <input inputMode="text" autoComplete="username" value={staffId} onChange={event=>setStaffId(event.target.value)} placeholder={directoryLoading?'讀取員工名單中…':'輸入登入編號'}/>
      </label>:null}

      <label className="stage0-field">
        <span>輸入 PIN</span>
        <input
          inputMode="numeric"
          autoComplete="current-password"
          maxLength={8}
          value={pin}
          onChange={event=>setPin(event.target.value.replace(/\D/g,'').slice(0,8))}
          placeholder="4–8 位數字"
          type="password"
        />
      </label>

      <PinKeypad value={pin} onChange={setPin} disabled={loading}/>

      {error?<div className="stage0-inline-message danger" role="alert">{error}</div>:null}

      <button className="stage0-primary" disabled={loading||pin.length<4} onClick={()=>void submit()}>
        {loading?'驗證中…':'登入'}
      </button>
      {!staff.length&&!directoryLoading?<button className="stage0-text-button" onClick={()=>void loadDirectory()}>重新讀取員工名單</button>:null}

    </section>
  </main>;
}
